import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth'd
  const [initializing, setInitializing] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // If we're returning from Emergent Google OAuth, let AuthCallback exchange
    // the session_id first — don't race it with a /me check that will 401.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setInitializing(false);
      return;
    }
    // Even without a stored JWT, we may already have a session_token cookie
    // from a previous Google login — do a /me check to be sure before deciding
    // the user is anonymous.
    refresh().finally(() => setInitializing(false));
  }, [refresh]);

  // Called by AuthCallback after a successful Google session exchange.
  const setSessionUser = useCallback((u) => {
    setUser(u);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("ica_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("ica_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("ica_token");
    setUser(false);
  };

  const updateProfile = async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider
      value={{ user, initializing, login, register, logout, refresh, updateProfile, setSessionUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
